// StockService.js
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { formatNumberWithComma, } from '../../utils/helpers';

// Helper functions
const getCounterName = (counterId, counterNamesData) => {
if (!counterNamesData || !Array.isArray(counterNamesData)) return 'Unknown';
const counter = counterNamesData.find(cnt => parseInt(cnt.id) === parseInt(counterId));
return counter ? counter.name : 'Unknown';
};

const getStatusName = (status) => {
return status === 1 ? 'Active' : 'Inactive';
};

const getBranchName = (branchId, branchNamesData) => {
if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
const branch = branchNamesData.find(b => parseInt(b.id) === parseInt(branchId));
return branch ? `${branch.name}` : 'Unknown';
};

const getStockLevelInfo = (balance) => {
const bal = parseFloat(balance) || 0;
if (bal <= 0) {
    return { level: 'Out of Stock', color: '#f44336' }; // Red
} else if (bal <= 10) {
    return { level: 'Low Stock', color: '#ff9800' }; // Orange
} else if (bal <= 50) {
    return { level: 'Normal Stock', color: '#4caf50' }; // Green
} else {
    return { level: 'High Stock', color: '#2196f3' }; // Blue
}
};

const formatCurrency = (amount) => {
const num = parseFloat(amount) || 0;
return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(num) + " "+"sh";
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
if (!text) return [''];
const words = String(text).split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for PDF (landscape mode) - UPDATED with only specified columns
const drawStocksTable = (doc, data, referenceData) => {
const pageWidth = doc.internal.pageSize.getWidth();
const pageHeight = doc.internal.pageSize.getHeight();
const margin = 20;
const rowHeight = 12;

// Column widths for landscape mode - only the specified columns
const colWidths = [25, 40, 35, 25, 25, 25, 30, 25, 35]; // Adjusted for specified columns
const headers = ['Stock Date', 'Item Name', 'Counter', 'Balance', 'Cost', 'Selling', 'Stock Level', 'Status', 'Branch'];

let currentY = margin + 50; // Start after header section
let pageNumber = 1;

// Draw table header on first page
doc.setFillColor(25, 118, 210);
doc.setTextColor(255, 255, 255);
doc.setFont(undefined, 'bold');

let xPos = margin;
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((stock, rowIndex) => {
    // Safely get values with defaults
    const counterName = getCounterName(stock.counter_id, referenceData?.counterNamesData || []);
    const branchName = getBranchName(stock.branch_id, referenceData?.branchNamesFilterData || []);
    const stockLevel = getStockLevelInfo(stock.balance);
    
    const balance = parseFloat(stock.balance) || 0;
    const cost = parseFloat(stock.cost) || 0;
    const selling = parseFloat(stock.selling) || 0;
    
    // Prepare row data with only specified columns
    const rowData = [
    stock.stock_date ? new Date(stock.stock_date).toLocaleDateString() : 'N/A',
    stock.name || 'N/A',
    counterName,
    formatNumberWithComma(balance),
    formatCurrency(cost),
    formatCurrency(selling),
    stockLevel.level,
    getStatusName(stock.status || 0),
    branchName
    ];

    // Calculate max lines needed for this row
    let maxLines = 1;
    const cellLines = rowData.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > pageHeight - 40) {
    doc.addPage('l');
    pageNumber++;
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
    doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
    if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    // Apply color to stock level column
    if (cellIndex === 6) { // Stock Level column
        doc.setTextColor(stockLevel.color);
    } else if (cellIndex === 7) { // Status column
        const status = lines[0];
        doc.setTextColor(status === 'Active' ? '#4caf50' : '#f44336');
    } else {
        doc.setTextColor(0, 0, 0);
    }
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    
    // Reset text color
    doc.setTextColor(0, 0, 0);
    });
    
    currentY += actualRowHeight;
});

return pageNumber;
};

// Excel export for Stocks - shows all attributes
export const exportStocksToExcel = (stocks, referenceData, filters = {}) => {
try {
    const { counterNamesData = [], branchNamesFilterData = [] } = referenceData || {};
    
    // Prepare data for Excel with all attributes
    const excelData = stocks.map((stock, index) => {
    const stockLevel = getStockLevelInfo(stock.balance);
    
    return {
        'No.': index + 1,
        'Stock Date': stock.stock_date ? new Date(stock.stock_date).toLocaleDateString() : 'N/A',
        'Item Name': stock.name || '',
        'Counter': getCounterName(stock.counter_id, counterNamesData),
        'Quantity': stock.quantity || 0,
        'Balance': parseFloat(stock.balance) || 0,
        'UOM': stock.uom || '',
        'Cost Price': parseFloat(stock.cost) || 0,
        'Selling Price': parseFloat(stock.selling) || 0,
        'Total Cost': parseFloat(stock.total_cost) || 0,
        'Total Selling': parseFloat(stock.total_selling) || 0,
        'Profit': ((parseFloat(stock.selling) || 0) - (parseFloat(stock.cost) || 0)) * (parseFloat(stock.balance) || 0),
        'Stock Level': stockLevel.level,
        'Status': getStatusName(stock.status || 0),
        'Branch': getBranchName(stock.branch_id, branchNamesFilterData),
        'Description': stock.description || '',
        'Notes': stock.notes || '',
        'Created Date': stock.created_at ? new Date(stock.created_at).toLocaleDateString() : 'N/A',
        'Updated Date': stock.updated_at ? new Date(stock.updated_at).toLocaleDateString() : 'N/A'
    };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Stocks');

    // Generate Excel file and download
    const fileName = `stocks_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Stocks Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// CSV export for Stocks - shows all attributes
export const exportStocksToCSV = (stocks, referenceData) => {
try {
    const { counterNamesData = [], branchNamesFilterData = [] } = referenceData || {};
    
    // CSV headers with all attributes
    const csvHeaders = [
    'No.',
    'Stock Date',
    'Item Name',
    'Counter',
    'Quantity',
    'Balance',
    'UOM',
    'Cost Price',
    'Selling Price',
    'Total Cost',
    'Total Selling',
    'Profit',
    'Stock Level',
    'Status',
    'Branch',
    'Description',
    'Notes',
    'Created Date',
    'Updated Date'
    ];

    // Create CSV rows with all attributes
    const csvRows = stocks.map((stock, index) => {
    const stockLevel = getStockLevelInfo(stock.balance);
    const profit = ((parseFloat(stock.selling) || 0) - (parseFloat(stock.cost) || 0)) * (parseFloat(stock.balance) || 0);
    
    return [
        index + 1,
        stock.stock_date ? new Date(stock.stock_date).toLocaleDateString() : '',
        stock.name || '',
        getCounterName(stock.counter_id, counterNamesData),
        stock.quantity || 0,
        parseFloat(stock.balance) || 0,
        stock.uom || '',
        parseFloat(stock.cost) || 0,
        parseFloat(stock.selling) || 0,
        parseFloat(stock.total_cost) || 0,
        parseFloat(stock.total_selling) || 0,
        profit,
        stockLevel.level,
        getStatusName(stock.status || 0),
        getBranchName(stock.branch_id, branchNamesFilterData),
        stock.description || '',
        stock.notes || '',
        stock.created_at ? new Date(stock.created_at).toLocaleDateString() : '',
        stock.updated_at ? new Date(stock.updated_at).toLocaleDateString() : ''
    ];
    });

    // Convert to CSV string
    const csvContent = [
    csvHeaders.join(','),
    ...csvRows.map(row => row.map(cell => {
        const cellValue = String(cell);
        // Escape commas and quotes in CSV
        if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
        return `"${cellValue.replace(/"/g, '""')}"`;
        }
        return cellValue;
    }).join(','))
    ].join('\r\n');

    // Create and download CSV file
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `stocks_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Stocks CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};

// PDF export for Stocks - UPDATED: landscape mode, only specified columns
export const exportStocksToPDFEnhanced = (stocks, referenceData, filters = {}) => {
return new Promise((resolve, reject) => {
    try {
    const { counterNamesData = [], branchNamesFilterData = [] } = referenceData || {};

    // Create PDF in landscape mode
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let currentY = margin;
    
    // Set document properties
    doc.setProperties({
        title: 'Stock Report',
        subject: 'Stock Export',
        author: companyInfo.name,
        keywords: 'stock, inventory, club, report',
        creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('CLUB STOCK INVENTORY REPORT', pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 6;
    doc.text(`Total Items: ${stocks.length}`, margin, currentY);
    currentY += 6;
    
    // Summary statistics
    const totalBalance = stocks.reduce((sum, stock) => sum + (parseFloat(stock.balance) || 0), 0);
    const totalCost = stocks.reduce((sum, stock) => sum + (parseFloat(stock.cost) || 0) * (parseFloat(stock.balance) || 0), 0);
    const totalSelling = stocks.reduce((sum, stock) => sum + (parseFloat(stock.selling) || 0) * (parseFloat(stock.balance) || 0), 0);
    const totalProfit = totalSelling - totalCost;
    
    doc.text(`Total Balance: ${formatNumberWithComma(totalBalance)}`, margin, currentY);
    currentY += 6;
    doc.text(`Total Cost Value: ${formatCurrency(totalCost)}`, margin, currentY);
    currentY += 6;
    doc.text(`Total Selling Value: ${formatCurrency(totalSelling)}`, margin, currentY);
    currentY += 6;
    doc.text(`Potential Profit: ${formatCurrency(totalProfit)}`, margin, currentY);
    currentY += 10;
    
    // Filters applied (if any)
    if (filters.counter_id || filters.stock_level || filters.status || filters.dateFrom || filters.dateTo) {
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        doc.text('Applied Filters:', margin, currentY);
        currentY += 6;
        
        if (filters.counter_id) {
        const counterName = getCounterName(filters.counter_id, counterNamesData);
        doc.text(`• Counter: ${counterName}`, margin + 10, currentY);
        currentY += 5;
        }
        if (filters.stock_level) {
        doc.text(`• Stock Level: ${filters.stock_level}`, margin + 10, currentY);
        currentY += 5;
        }
        if (filters.status) {
        doc.text(`• Status: ${getStatusName(filters.status)}`, margin + 10, currentY);
        currentY += 5;
        }
        if (filters.dateFrom) {
        const fromDate = filters.dateFrom ? new Date(filters.dateFrom).toLocaleDateString() : 'N/A';
        doc.text(`• From: ${fromDate}`, margin + 10, currentY);
        currentY += 5;
        }
        if (filters.dateTo) {
        const toDate = filters.dateTo ? new Date(filters.dateTo).toLocaleDateString() : 'N/A';
        doc.text(`• To: ${toDate}`, margin + 10, currentY);
        currentY += 5;
        }
        currentY += 10;
    }
    
    // Draw the table with only specified columns
    const totalPages = drawStocksTable(doc, stocks, referenceData);
    
    // Add footer with page numbers
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        doc.text(`Generated by ${companyInfo.name}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
    }
    
    // Save PDF file
    const fileName = `stocks_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    resolve({ success: true, fileName });
    } catch (error) {
    console.error('Stocks PDF export failed:', error);
    reject(new Error('Failed to export to PDF: ' + error.message));
    }
});
};

